import React, { useEffect, useState } from 'react';

interface MemorySnapshot {
  component: string;
  rss_kb: number;
  vm_size_kb: number;
  threads: number;
  fd_count: number;
  recorded_at: string;
}

interface MemoryChartProps {
  nodeId: string;
  enabled: boolean;
  onToggle: () => void;
}

const COLORS: Record<string, string> = {
  momanager: '#3b82f6',   // blue
  moclient: '#22c55e',    // green
  moplayer: '#f97316',    // orange
  mpp_renderer: '#a855f7', // purple
};

const PERIODS = [
  { label: '1h', hours: 1 },
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
];

const MemoryChart: React.FC<MemoryChartProps> = ({ nodeId, enabled, onToggle }) => {
  const [data, setData] = useState<MemorySnapshot[]>([]);
  const [hours, setHours] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/moclient-test/memory-history/${nodeId}?hours=${hours}`);
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) {
      console.error('Failed to fetch memory history:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (enabled) {
      fetchData();
      const interval = setInterval(fetchData, 60_000); // refresh every 60s
      return () => clearInterval(interval);
    }
  }, [nodeId, hours, enabled]);

  if (!enabled) {
    return (
      <div className="mt-2 p-2 bg-gray-800 rounded border border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-gray-400 text-xs">Memory Profiling</span>
          <button
            onClick={onToggle}
            className="px-2 py-0.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded"
          >
            Enable
          </button>
        </div>
      </div>
    );
  }

  // Group data by component
  const components = [...new Set(data.map(d => d.component))];
  const byComponent: Record<string, MemorySnapshot[]> = {};
  for (const comp of components) {
    byComponent[comp] = data.filter(d => d.component === comp);
  }

  // Chart dimensions
  const W = 600, H = 200, PAD = { top: 20, right: 80, bottom: 30, left: 50 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  // Time range
  const times = data.map(d => new Date(d.recorded_at).getTime());
  const minT = times.length > 0 ? Math.min(...times) : Date.now() - hours * 3600_000;
  const maxT = times.length > 0 ? Math.max(...times) : Date.now();
  const rangeT = maxT - minT || 1;

  // RSS range (in MB)
  const allRss = data.map(d => d.rss_kb / 1024);
  const maxRss = allRss.length > 0 ? Math.max(...allRss) * 1.15 : 100;
  const minRss = 0;
  const rangeRss = maxRss - minRss || 1;

  const toX = (t: number) => PAD.left + ((t - minT) / rangeT) * plotW;
  const toY = (mb: number) => PAD.top + plotH - ((mb - minRss) / rangeRss) * plotH;

  // Y axis labels
  const yTicks = 5;
  const yLabels = Array.from({ length: yTicks + 1 }, (_, i) =>
    Math.round(minRss + (rangeRss * i) / yTicks)
  );

  // X axis labels
  const xTicks = 4;
  const xLabels = Array.from({ length: xTicks + 1 }, (_, i) => {
    const t = minT + (rangeT * i) / xTicks;
    const d = new Date(t);
    return { t, label: `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}` };
  });

  // Latest values for legend
  const latest: Record<string, MemorySnapshot | undefined> = {};
  for (const comp of components) {
    const arr = byComponent[comp];
    latest[comp] = arr[arr.length - 1];
  }

  return (
    <div className="mt-2 p-2 bg-gray-800 rounded border border-gray-700">
      <div className="flex items-center justify-between mb-1">
        <span className="text-gray-300 text-xs font-bold">Memory (RSS)</span>
        <div className="flex items-center gap-1">
          {PERIODS.map(p => (
            <button
              key={p.hours}
              onClick={() => setHours(p.hours)}
              className={`px-1.5 py-0.5 text-xs rounded ${
                hours === p.hours ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={onToggle}
            className="ml-2 px-2 py-0.5 text-xs bg-red-900 hover:bg-red-800 text-red-300 rounded"
          >
            Disable
          </button>
        </div>
      </div>

      {loading && data.length === 0 ? (
        <div className="text-center text-gray-500 py-8 text-xs">Loading...</div>
      ) : data.length === 0 ? (
        <div className="text-center text-gray-500 py-8 text-xs">No data yet. Waiting for first snapshot (~60s).</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: '250px' }}>
          {/* Grid lines */}
          {yLabels.map(mb => (
            <g key={mb}>
              <line x1={PAD.left} y1={toY(mb)} x2={W - PAD.right} y2={toY(mb)}
                stroke="#374151" strokeWidth="0.5" />
              <text x={PAD.left - 4} y={toY(mb) + 3} textAnchor="end"
                fill="#6b7280" fontSize="9">{mb}M</text>
            </g>
          ))}

          {/* X axis labels */}
          {xLabels.map(({ t, label }) => (
            <text key={t} x={toX(t)} y={H - 5} textAnchor="middle"
              fill="#6b7280" fontSize="9">{label}</text>
          ))}

          {/* Data lines */}
          {components.map(comp => {
            const points = byComponent[comp];
            if (points.length < 2) return null;
            const d = points.map(p =>
              `${toX(new Date(p.recorded_at).getTime())},${toY(p.rss_kb / 1024)}`
            ).join(' L ');
            return (
              <path key={comp} d={`M ${d}`}
                fill="none" stroke={COLORS[comp] || '#888'} strokeWidth="1.5" />
            );
          })}

          {/* Legend with latest values */}
          {components.map((comp, i) => {
            const l = latest[comp];
            const y = PAD.top + 12 + i * 14;
            return (
              <g key={comp}>
                <rect x={W - PAD.right + 6} y={y - 8} width={8} height={8} rx="1"
                  fill={COLORS[comp] || '#888'} />
                <text x={W - PAD.right + 18} y={y} fill="#d1d5db" fontSize="9">
                  {comp}: {l ? `${(l.rss_kb / 1024).toFixed(0)}M` : '-'}
                </text>
              </g>
            );
          })}
        </svg>
      )}

      {/* Current stats table */}
      {components.length > 0 && (
        <div className="mt-1 grid grid-cols-4 gap-x-2 text-xs text-gray-400">
          <span className="font-bold text-gray-500">Component</span>
          <span className="font-bold text-gray-500">RSS</span>
          <span className="font-bold text-gray-500">Threads</span>
          <span className="font-bold text-gray-500">FDs</span>
          {components.map(comp => {
            const l = latest[comp];
            if (!l) return null;
            return (
              <React.Fragment key={comp}>
                <span style={{ color: COLORS[comp] || '#888' }}>{comp}</span>
                <span>{(l.rss_kb / 1024).toFixed(1)} MB</span>
                <span>{l.threads}</span>
                <span>{l.fd_count}</span>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MemoryChart;
